import { z } from 'zod'
import { IDEMPOTENCY_KEY, portCodes } from '@/features/consignment-form/contract'
import type { Consignment, Item } from '@/features/consignment-form/schema'

export const DRAFT_KEY = 'cargo-sark.consignment-draft'
const EDITOR_KEY = 'cargo-sark.item-editor-draft'
export const ATTEMPT_KEY = 'cargo-sark.consignment-attempt'

/** Stored with every draft: a draft written against another shape is dropped, not migrated. */
export const DRAFT_VERSION = 5

const partyFields = {
  name: z.string(),
  surname: z.string(),
  contactNumber: z.string(),
  email: z.string(),
  companyName: z.string(),
  street: z.string(),
  postcode: z.string(),
  locality: z.string(),
}

const partyDraftSchema = z.discriminatedUnion('legalEntity', [
  z.object({ ...partyFields, legalEntity: z.literal(false) }),
  z.object({ ...partyFields, legalEntity: z.literal(true) }),
])

const itemDraftSchema = z.object({
  qty: z.string(),
  category: z.string(),
  description: z.string(),
  weightKg: z.string(),
  lengthCm: z.string(),
  widthCm: z.string(),
  heightCm: z.string(),
}) satisfies z.ZodType<Item>

const draftValuesSchema = z.object({
  departurePort: z.enum(portCodes),
  destinationPort: z.enum(portCodes),
  readyFrom: z.string(),
  paidBy: z.enum(['SHIPPER', 'CONSIGNEE']),
  paymentMethod: z.enum(['CARD', 'INVOICE']),
  shipper: partyDraftSchema,
  consignee: partyDraftSchema,
  items: z.array(itemDraftSchema),
}) satisfies z.ZodType<Consignment>

const draftSchema = z.object({
  version: z.literal(DRAFT_VERSION),
  values: draftValuesSchema,
})

const editorDraftSchema = z.object({
  version: z.literal(DRAFT_VERSION),
  index: z.number().int().nonnegative().nullable(),
  values: itemDraftSchema,
})

export type EditorDraft = Pick<z.infer<typeof editorDraftSchema>, 'index' | 'values'>

const quietly = <T>(action: () => T) => {
  try {
    return action()
  } catch {
    return undefined
  }
}

export function readDraft() {
  return quietly(() => {
    const stored = sessionStorage.getItem(DRAFT_KEY)
    if (stored === null) return undefined

    const draft = draftSchema.safeParse(JSON.parse(stored))
    return draft.success ? draft.data.values : undefined
  })
}

export function writeDraft(values: Consignment) {
  quietly(() => sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ version: DRAFT_VERSION, values })))
}

export function readEditorDraft() {
  return quietly(() => {
    const stored = sessionStorage.getItem(EDITOR_KEY)
    if (stored === null) return undefined

    const draft = editorDraftSchema.safeParse(JSON.parse(stored))
    return draft.success ? { index: draft.data.index, values: draft.data.values } : undefined
  })
}

export function writeEditorDraft(editor: EditorDraft) {
  quietly(() => sessionStorage.setItem(EDITOR_KEY, JSON.stringify({ version: DRAFT_VERSION, ...editor })))
}

export function clearEditorDraft() {
  quietly(() => sessionStorage.removeItem(EDITOR_KEY))
}

export function clearDraft() {
  quietly(() => sessionStorage.removeItem(DRAFT_KEY))
}

const attemptSchema = z.object({
  key: z.string().regex(IDEMPOTENCY_KEY),
  body: z.string().min(1),
})

export type CreateAttempt = z.infer<typeof attemptSchema>

export function readAttempt() {
  return quietly(() => {
    const stored = sessionStorage.getItem(ATTEMPT_KEY)
    if (stored === null) return undefined
    const attempt = attemptSchema.safeParse(JSON.parse(stored))
    if (attempt.success) JSON.parse(attempt.data.body)
    return attempt.success ? attempt.data : undefined
  })
}

/** A damaged snapshot may describe an already created booking; do not replace its key. */
export function hasSavedAttempt() {
  return quietly(() => sessionStorage.getItem(ATTEMPT_KEY) !== null)
}

export function writeAttempt(attempt: CreateAttempt) {
  return (
    quietly(() => {
      sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(attempt))
      return true
    }) === true
  )
}

export function clearAttempt() {
  quietly(() => sessionStorage.removeItem(ATTEMPT_KEY))
}
